// ─────────────────────────────────────────────────────────────────
// Glass-i — Purchase Order create / edit / view page (Stage 6P.2).
//
// Routes:
//   /erp/purchase/orders/new   → create mode (no :id)
//   /erp/purchase/orders/:id   → edit / view mode (status-gated)
//
// The page is the only PO authoring surface for Stage 6P.2. It hosts
// a sticky toolbar, a compact two-column header form (PO meta on the
// left, instant totals on the right), and an inline-CRUD line grid
// with item-type-aware columns (W/H/SQMT/SQFT for GLASS;
// LENGTH/LENGTH UOM/TOTAL LENGTH for PROFILE/UPVC/ALUMINIUM; just
// QTY for HARDWARE/ACCESSORY/SERVICE/JOBWORK/OTHER).
//
// Money math posture:
//   • Frontend mirrors the backend calculation helpers from
//     purchaseHelpers.js so the operator sees live totals as they
//     type. Every input change re-runs:
//         normalizePurchaseLineByItemType → calculatePurchaseOrderLineAmount
//         → calculatePoTotals.
//   • Backend remains the source of truth — every save round-trips
//     through purchase.validation + purchaseCalculation.service. We
//     pull the recomputed numbers back into local state from the
//     server response so screen + DB stay aligned to the paisa.
//
// Status-aware UI:
//   • DRAFT  → everything editable; Save Draft, Place PO, Cancel PO,
//              Delete (separate page-level action — out of scope here).
//   • PLACED / PARTIALLY_RECEIVED / CLOSED / CANCELLED → read-only.
//   • Toolbar action gates use the can* helpers in purchaseHelpers so
//     the UI never offers a CTA the backend would refuse.
//
// Style: mirrors the compact density of GlassCategoryMasterPage —
// slate-50 headers, 22 px row stride, sticky toolbar, ⋯ row-actions.
// All CSS lives in the co-located stylesheet; no global edits.
// ─────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  DatePicker,
  Dropdown,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Spin,
  Table,
  message
} from 'antd';
import {
  CheckCircleOutlined,
  DeleteOutlined,
  EnvironmentOutlined,
  FileTextOutlined,
  LockOutlined,
  MoreOutlined,
  PlusOutlined,
  SaveOutlined,
  SendOutlined,
  ShopOutlined,
  StopOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';

import { purchaseOrdersApi } from './purchaseApi';
import ItemMasterSelect from './ItemMasterSelect';
import SupplierMasterSelect from './SupplierMasterSelect';
import { loadManualChargeLookupsSafe } from '../masters/manualCharges/manualChargeApi';
import {
  ITEM_TYPE_OPTIONS,
  LENGTH_BASED_UOMS,
  buildPurchaseOrderPayload,
  calculatePoTotals,
  calculatePurchaseOrderLineAmount,
  canCancelPurchaseOrder,
  canClosePurchaseOrder,
  canEditPurchaseOrder,
  canPlacePurchaseOrder,
  normalizePurchaseLineByItemType,
  normalizePurchaseOrder,
  statusLabel,
  validatePurchaseOrder
} from './purchaseHelpers';
import './PurchaseOrderFormPage.css';

// ─────────────────────────────────────────────────────────────────
// Static option lists. The full UOM list is small enough to inline
// here without a backend lookup; it matches the validator's allowed
// values. GST rates are the standard CBIC slabs.
// ─────────────────────────────────────────────────────────────────
const UOM_OPTIONS = Object.freeze([
  'PCS', 'NOS', 'KG', 'GRM', 'LTR', 'MTR', 'RFT', 'RMT',
  'SQMT', 'SQFT', 'BOX', 'SET', 'PAIR', 'BUNDLE', 'DOZ', 'ROLL', 'SHEET'
].map((v) => ({ value: v, label: v })));

const LENGTH_UOM_OPTIONS = Object.freeze(
  LENGTH_BASED_UOMS.map((v) => ({ value: v, label: v }))
);

const GST_RATE_OPTIONS = Object.freeze(
  [0, 0.1, 0.25, 1, 3, 5, 12, 18, 28].map((v) => ({
    value: v,
    label: `${v}%`
  }))
);

// Item-type families that drive column visibility on each grid row.
const GLASS_TYPES   = new Set(['GLASS']);
const LENGTH_TYPES  = new Set(['PROFILE', 'UPVC', 'ALUMINIUM']);

// Status dot colours — a calm header signal that doesn't wash the cell
// the way a filled tag does. Aligned with the PO / GRN / RFQ status-dot
// palette for cross-module consistency. Presentation only.
const STATUS_DOT = {
  DRAFT:              '#94a3b8',
  PLACED:             '#1877f2',
  PARTIALLY_RECEIVED: '#f59e0b',
  RECEIVED:           '#16a34a',
  CLOSED:             '#16a34a',
  CANCELLED:          '#dc2626'
};

// Item-type families exempt from the Item Master lock (free-text allowed).
// Everything else must be picked from the master. Mirrors the backend
// MASTER_OPTIONAL_ITEM_TYPES in services/erp/purchase/itemMasterGuard.js.
const MASTER_OPTIONAL_TYPES = new Set(['SERVICE', 'OTHER']);

// Today as a dayjs object — used as the default for new POs.
function todayDayjs() {
  return dayjs().startOf('day');
}

// Convert a server ISO date string to a dayjs (or null).
function isoToDayjs(v) {
  if (v === null || v === undefined || v === '') return null;
  const d = dayjs(v);
  return d.isValid() ? d : null;
}

// Convert a dayjs / null back to YYYY-MM-DD for the payload builder.
function dayjsToIso(v) {
  if (!v) return null;
  if (typeof v === 'string') return v;
  if (dayjs.isDayjs(v)) return v.format('YYYY-MM-DD');
  const d = dayjs(v);
  return d.isValid() ? d.format('YYYY-MM-DD') : null;
}

// Stable client-row id minter for freshly-added lines.
let _localRowSeq = 0;
function nextLocalRowId() {
  _localRowSeq += 1;
  return `po-local-${Date.now()}-${_localRowSeq}`;
}

function makeEmptyLine() {
  // Defaults per the brief: OTHER / qty 1 / UOM PCS / GST 0 / disc 0.
  // Run through the line calculator immediately so the readonly
  // derived columns (taxable/cgst/sgst/igst/line_total) start at 0
  // instead of undefined.
  const seed = {
    _clientRowId:     nextLocalRowId(),
    _dirty:           true,
    item_type:        'OTHER',
    item_name:        '',
    qty:              1,
    uom:              'PCS',
    rate:             0,
    discount_percent: 0,
    gst_rate:         0,
    remarks:          ''
  };
  return calculatePurchaseOrderLineAmount(seed, {});
}

// Header defaults for create mode.
function makeEmptyHeader() {
  return {
    id:                   null,
    poNo:                 null,
    poDate:               todayDayjs(),
    expectedDeliveryDate: null,
    status:               'DRAFT',
    supplierId:           null,
    supplierName:         '',
    supplierGstin:        '',
    supplierContact:      '',
    supplierAddress:      '',
    supplierStateCode:    '',
    companyStateCode:     '',
    sourcePrId:           null,
    prNumberSnapshot:     null,
    deliveryLocation:     '',
    paymentTerms:         '',
    deliveryTerms:        '',
    freightTerms:         '',
    remarks:              '',
    roundOff:             0,
    subtotal:             0,
    discountAmount:       0,
    taxableAmount:        0,
    cgstAmount:           0,
    sgstAmount:           0,
    igstAmount:           0,
    totalTax:             0,
    grandTotal:           0,
    lines:                [makeEmptyLine()]
  };
}

// Convert the editor state shape into the form values the AntD Form
// uses for the *header* fields. Lines live in their own state so they
// don't fight AntD's array-field reconciliation. Returns a plain
// object; dayjs values stay as dayjs for the picker.
function headerToFormValues(h) {
  return {
    poDate:               h.poDate,
    expectedDeliveryDate: h.expectedDeliveryDate,
    supplierName:         h.supplierName || '',
    supplierGstin:        h.supplierGstin || '',
    supplierContact:      h.supplierContact || '',
    supplierAddress:      h.supplierAddress || '',
    supplierStateCode:    h.supplierStateCode || '',
    companyStateCode:     h.companyStateCode || '',
    deliveryLocation:     h.deliveryLocation || '',
    paymentTerms:         h.paymentTerms || '',
    deliveryTerms:        h.deliveryTerms || '',
    freightTerms:         h.freightTerms || '',
    remarks:              h.remarks || ''
  };
}

// Currency formatter — 2dp, en-IN, no symbol so the columns line up.
const _fmt = new Intl.NumberFormat('en-IN', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});
function money(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '0.00';
  return _fmt.format(v);
}
const _fmt4 = new Intl.NumberFormat('en-IN', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 4
});
function dim(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '';
  if (v === 0) return '';
  return _fmt4.format(v);
}

export default function PurchaseOrderFormPage() {
  const { id }   = useParams();
  const navigate = useNavigate();
  const isCreate = !id;

  const [form]    = Form.useForm();
  const [header,  setHeader]  = useState(() => makeEmptyHeader());
  const [lines,   setLines]   = useState(() => header.lines);
  const [roundOff, setRoundOff] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving,  setSaving]  = useState(false);
  const [acting,  setActing]  = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  // Ad-hoc manual Other Charges (post-tax, no GST) + Manual Charge Master
  // lookup for the picker.
  const [otherCharges, setOtherCharges] = useState([]);
  const [chargeOptions, setChargeOptions] = useState([]);
  // Structured delivery address (Ship To) — same shape as the proforma.
  const [shipTo, setShipTo] = useState({});
  // Structured supplier address (Bill From) + the buyer's company state code
  // (drives the GST intra/inter split). Both held as local state now that the
  // supplier block is a snapshot rather than flat Form fields.
  const [billFrom, setBillFrom] = useState({});
  const [companyStateCode, setCompanyStateCode] = useState('');

  // We keep a ref to the latest header so async callbacks can read it
  // without retriggering effects.
  const headerRef = useRef(header);
  headerRef.current = header;

  // ── Read-only gate ──────────────────────────────────────────────
  const isEditable = useMemo(() => {
    if (isCreate) return true;
    return canEditPurchaseOrder(header);
  }, [isCreate, header]);

  const isLocked = !isEditable;

  // ── Load on mount (edit mode) ──────────────────────────────────
  const loadPo = useCallback(async () => {
    if (isCreate) return;
    setLoading(true);
    try {
      const raw = await purchaseOrdersApi.get(id);
      const norm = normalizePurchaseOrder(raw);
      // Re-hydrate dayjs date fields for the pickers.
      const hydrated = Object.assign({}, norm, {
        poDate:               isoToDayjs(norm.poDate),
        expectedDeliveryDate: isoToDayjs(norm.expectedDeliveryDate)
      });
      setHeader(hydrated);
      setRoundOff(Number(norm.roundOff) || 0);
      // Lines arrive already normalised by normalizePurchaseOrder; we
      // run them through the line calculator once so the readonly
      // derived columns are guaranteed populated from the same code
      // path the UI uses on edit.
      const taxCtx = {
        supplier_state_code: norm.supplierStateCode || null,
        company_state_code:  norm.companyStateCode  || null
      };
      const recomputed = (norm.lines || []).map((ln) =>
        Object.assign({}, ln, calculatePurchaseOrderLineAmount(ln, taxCtx)));
      setLines(recomputed.length ? recomputed : [makeEmptyLine()]);
      setOtherCharges(Array.isArray(norm.otherCharges) ? norm.otherCharges : []);
      setShipTo(norm.shipTo && typeof norm.shipTo === 'object' ? norm.shipTo : {});
      setBillFrom(norm.billFrom && typeof norm.billFrom === 'object' ? norm.billFrom : {});
      setCompanyStateCode(norm.companyStateCode || '');
      form.setFieldsValue(headerToFormValues(hydrated));
    } catch (e) {
      message.error(e?.response?.data?.error?.message
        || e.message || 'Failed to load purchase order.');
    } finally {
      setLoading(false);
    }
  }, [id, isCreate, form]);

  useEffect(() => {
    if (isCreate) {
      // Seed the AntD form with the create-mode defaults.
      form.setFieldsValue(headerToFormValues(headerRef.current));
    } else {
      loadPo();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // ── Totals recompute — runs whenever lines, roundOff, or the tax
  //    context (supplier/company state codes) change. We deliberately
  //    do NOT mutate `lines` here; the line calculator already updated
  //    them at the point of edit. This effect just rolls up the header.
  const supplierState = (billFrom.stateCode || '').toUpperCase() || null;
  const companyState  = (companyStateCode || '').toUpperCase() || null;

  const totals = useMemo(() => {
    return calculatePoTotals(lines, {
      round_off:            roundOff,
      supplier_state_code:  supplierState || header.supplierStateCode,
      company_state_code:   companyState  || header.companyStateCode,
      other_charges:        otherCharges
    });
  }, [lines, roundOff, supplierState, companyState,
      header.supplierStateCode, header.companyStateCode, otherCharges]);

  // Load the Manual Charge Master lookup once for the Other-charges picker.
  useEffect(() => {
    let alive = true;
    loadManualChargeLookupsSafe().then((list) => {
      if (alive) setChargeOptions(Array.isArray(list) ? list : []);
    });
    return () => { alive = false; };
  }, []);

  // ── Other-charge row helpers (presentation state; recomputed on save) ──
  const addCharge = useCallback(() => {
    setOtherCharges((prev) => [...prev, {
      _clientRowId:   `poc-new-${Date.now()}-${prev.length}`,
      manualChargeId: null,
      chargeName:     '',
      area:           0,
      rate:           0
    }]);
  }, []);
  const patchCharge = useCallback((rowId, partial) => {
    setOtherCharges((prev) => prev.map((c) =>
      (c._clientRowId === rowId ? Object.assign({}, c, partial) : c)));
  }, []);
  const removeCharge = useCallback((rowId) => {
    setOtherCharges((prev) => prev.filter((c) => c._clientRowId !== rowId));
  }, []);
  // Trailing "Pick charge…" adder row → append a new charge on pick.
  const addChargeWithName = useCallback((value) => {
    if (!value) return;
    const opt = chargeOptions.find((o) => (o.chargeName || o.charge_name) === value);
    setOtherCharges((prev) => [...prev, {
      _clientRowId:   `poc-new-${Date.now()}-${prev.length}`,
      manualChargeId: opt ? (opt.id || null) : null,
      chargeName:     value,
      area:           0,
      rate:           opt && Number(opt.defaultRate ?? opt.default_rate) > 0
        ? Number(opt.defaultRate ?? opt.default_rate) : 0
    }]);
  }, [chargeOptions]);

  // Pick a charge from the master → fill name + default rate.
  const pickCharge = useCallback((rowId, value) => {
    const opt = chargeOptions.find((o) =>
      (o.chargeName || o.charge_name) === value);
    patchCharge(rowId, {
      chargeName:     value || '',
      manualChargeId: opt ? (opt.id || null) : null,
      rate:           opt && Number(opt.defaultRate ?? opt.default_rate) > 0
        ? Number(opt.defaultRate ?? opt.default_rate)
        : undefined
    });
  }, [chargeOptions, patchCharge]);

  const chargeSelectOptions = useMemo(
    () => chargeOptions.map((o) => {
      const name = o.chargeName || o.charge_name;
      return { value: name, label: name };
    }), [chargeOptions]);

  const chargeAmount = (c) => {
    const area = Number(c.area) || 0;
    const rate = Number(c.rate) || 0;
    return (area > 0 ? area : 1) * rate;
  };

  // Ship To (delivery address) field patcher.
  const patchShipTo = useCallback((key, value) => {
    setShipTo((prev) => Object.assign({}, prev, { [key]: value }));
  }, []);
  // Supplier (Bill From) field patcher.
  const patchBillFrom = useCallback((key, value) => {
    setBillFrom((prev) => Object.assign({}, prev, { [key]: value }));
  }, []);

  // ── Line mutation helpers ──────────────────────────────────────
  // When supplier/company state codes change in the header we want
  // the existing lines to flip CGST/SGST ↔ IGST without the operator
  // having to retype rates. Re-run the line calculator across all
  // rows on tax-context shifts.
  useEffect(() => {
    setLines((prev) => prev.map((ln) => Object.assign({}, ln,
      calculatePurchaseOrderLineAmount(ln, {
        supplier_state_code: supplierState || null,
        company_state_code:  companyState  || null
      }))));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplierState, companyState]);

  const patchLine = useCallback((clientRowId, partial) => {
    setLines((prev) => prev.map((ln) => {
      if (ln._clientRowId !== clientRowId) return ln;
      const merged = Object.assign({}, ln, partial, { _dirty: true });
      // Normalise + recompute every time a math-relevant field moves.
      const normalised = normalizePurchaseLineByItemType(merged);
      const computed   = calculatePurchaseOrderLineAmount(normalised, {
        supplier_state_code: supplierState || null,
        company_state_code:  companyState  || null
      });
      return Object.assign({}, normalised, computed, {
        _clientRowId: ln._clientRowId,
        _dirty:       true
      });
    }));
  }, [supplierState, companyState]);

  const addLine = useCallback(() => {
    setLines((prev) => [...prev, makeEmptyLine()]);
  }, []);

  const removeLine = useCallback((clientRowId) => {
    setLines((prev) => {
      const next = prev.filter((ln) => ln._clientRowId !== clientRowId);
      // Guarantee at least one line in DRAFT.
      return next.length === 0 ? [makeEmptyLine()] : next;
    });
  }, []);

  const duplicateLine = useCallback((clientRowId) => {
    setLines((prev) => {
      const idx = prev.findIndex((ln) => ln._clientRowId === clientRowId);
      if (idx < 0) return prev;
      const src = prev[idx];
      const copy = Object.assign({}, src, {
        _clientRowId: nextLocalRowId(),
        _dirty:       true,
        id:           null,
        line_no:      undefined
      });
      const next = [...prev];
      next.splice(idx + 1, 0, copy);
      return next;
    });
  }, []);

  // ── Build a fresh state object to feed the payload builder ─────
  function snapshot() {
    const v = form.getFieldsValue();
    return Object.assign({}, header, v, {
      id:        header.id,
      status:    header.status,
      poDate:    dayjsToIso(v.poDate),
      expectedDeliveryDate: dayjsToIso(v.expectedDeliveryDate),
      roundOff,
      otherCharges,
      shipTo,
      billFrom,
      // Supplier flat fields are derived from the Bill From snapshot so the
      // payload builder + validator (which read supplierName / state codes)
      // stay in sync with the new structured block.
      supplierName:      (billFrom.name || '').trim(),
      supplierGstin:     (billFrom.gstin || '').trim(),
      supplierContact:   (billFrom.mobile || '').trim(),
      supplierAddress:   (billFrom.addressLine1 || '').trim(),
      supplierStateCode: (billFrom.stateCode || '').toUpperCase(),
      companyStateCode:  (companyStateCode || '').toUpperCase(),
      lines
    });
  }

  // ── Save handlers ──────────────────────────────────────────────
  async function saveDraft() {
    if (isLocked) {
      message.warning('This purchase order is locked.');
      return;
    }
    try {
      await form.validateFields();
    } catch (_) {
      message.error('Please fix the highlighted header fields.');
      return;
    }

    const state = snapshot();
    const { ok, errors } = validatePurchaseOrder(state);
    if (!ok) {
      message.error(errors[0].message);
      return;
    }
    const payload = buildPurchaseOrderPayload(state);
    setSaving(true);
    try {
      let saved;
      if (isCreate) {
        saved = await purchaseOrdersApi.create(payload);
      } else {
        saved = await purchaseOrdersApi.update(header.id, payload);
      }
      const norm = normalizePurchaseOrder(saved);
      message.success(isCreate
        ? `Purchase order ${norm.poNo || ''} saved as draft.`
        : `Purchase order ${norm.poNo || ''} updated.`);
      if (isCreate && norm.id) {
        navigate(`/erp/purchase/orders/${norm.id}`, { replace: true });
      } else {
        // Re-hydrate from server numbers — backend is source of truth.
        const hydrated = Object.assign({}, norm, {
          poDate:               isoToDayjs(norm.poDate),
          expectedDeliveryDate: isoToDayjs(norm.expectedDeliveryDate)
        });
        setHeader(hydrated);
        setRoundOff(Number(norm.roundOff) || 0);
        const taxCtx = {
          supplier_state_code: norm.supplierStateCode || null,
          company_state_code:  norm.companyStateCode  || null
        };
        const recomputed = (norm.lines || []).map((ln) =>
          Object.assign({}, ln, calculatePurchaseOrderLineAmount(ln, taxCtx)));
        setLines(recomputed.length ? recomputed : [makeEmptyLine()]);
        setOtherCharges(Array.isArray(norm.otherCharges) ? norm.otherCharges : []);
        setShipTo(norm.shipTo && typeof norm.shipTo === 'object' ? norm.shipTo : {});
        setBillFrom(norm.billFrom && typeof norm.billFrom === 'object' ? norm.billFrom : {});
        setCompanyStateCode(norm.companyStateCode || '');
        form.setFieldsValue(headerToFormValues(hydrated));
      }
    } catch (e) {
      message.error(e?.response?.data?.error?.message
        || e.message || 'Failed to save purchase order.');
    } finally {
      setSaving(false);
    }
  }

  async function placePo() {
    if (!header.id) {
      message.warning('Save the draft before placing the PO.');
      return;
    }
    if (!canPlacePurchaseOrder(header)) {
      message.warning('Only DRAFT purchase orders can be placed.');
      return;
    }
    Modal.confirm({
      title: 'Place this purchase order?',
      content:
        'Placing the PO locks the header and lines from further edits. '
        + 'No stock or accounting entries are posted at this step.',
      okText: 'Place PO',
      cancelText: 'Back',
      onOk: async () => {
        setActing(true);
        try {
          const out = await purchaseOrdersApi.place(header.id);
          const norm = normalizePurchaseOrder(out);
          message.success(`Purchase order ${norm.poNo || ''} placed.`);
          await loadPo();
        } catch (e) {
          message.error(e?.response?.data?.error?.message
            || e.message || 'Failed to place purchase order.');
        } finally {
          setActing(false);
        }
      }
    });
  }

  async function closePo() {
    if (!header.id) return;
    if (!canClosePurchaseOrder(header)) {
      message.warning('Only PLACED purchase orders can be closed.');
      return;
    }
    Modal.confirm({
      title: 'Close this purchase order?',
      content: 'Closing prevents any further GRN against this PO.',
      okText: 'Close PO',
      cancelText: 'Back',
      onOk: async () => {
        setActing(true);
        try {
          const out = await purchaseOrdersApi.close(header.id);
          const norm = normalizePurchaseOrder(out);
          message.success(`Purchase order ${norm.poNo || ''} closed.`);
          await loadPo();
        } catch (e) {
          message.error(e?.response?.data?.error?.message
            || e.message || 'Failed to close purchase order.');
        } finally {
          setActing(false);
        }
      }
    });
  }

  function openCancelModal() {
    if (!header.id) return;
    if (!canCancelPurchaseOrder(header)) {
      message.warning('Only DRAFT or PLACED purchase orders can be cancelled.');
      return;
    }
    setCancelReason('');
    setCancelOpen(true);
  }

  async function confirmCancel() {
    setActing(true);
    try {
      const out = await purchaseOrdersApi.cancel(header.id,
        { reason: cancelReason || undefined });
      const norm = normalizePurchaseOrder(out);
      message.success(`Purchase order ${norm.poNo || ''} cancelled.`);
      setCancelOpen(false);
      await loadPo();
    } catch (e) {
      message.error(e?.response?.data?.error?.message
        || e.message || 'Failed to cancel purchase order.');
    } finally {
      setActing(false);
    }
  }

  // ── Adaptive dimension columns ─────────────────────────────────
  // Glass W/H/SQMT/SQFT and profile Length/L-UOM/Total-L columns only
  // earn their horizontal space when a line of that family is present.
  // Derived from the live lines so the grid widens / narrows as item
  // types change — presentation only (the per-row renderers already
  // blank the cell for non-matching types).
  const hasGlassLines  = useMemo(
    () => lines.some((l) => GLASS_TYPES.has(l.item_type)), [lines]);
  const hasLengthLines = useMemo(
    () => lines.some((l) => LENGTH_TYPES.has(l.item_type)), [lines]);

  // ── Line columns ───────────────────────────────────────────────
  const columns = useMemo(() => {
    const numCellProps = { className: 'po-cell-num' };
    const readonlyCellProps = { className: 'po-cell-readonly po-cell-num' };

    return [
      { title: 'Item', children: [
      {
        title: 'Item Type', dataIndex: 'item_type', width: 130,
        render: (v, row) => (
          <Select
            size="small"
            variant="borderless"
            value={v || undefined}
            disabled={isLocked}
            style={{ width: '100%' }}
            options={ITEM_TYPE_OPTIONS}
            onChange={(val) => patchLine(row._clientRowId, {
              item_type: val,
              // Reset item identity when crossing the product/service
              // boundary (product must be re-picked from master; SERVICE/
              // OTHER drops the master id and reverts to free-text).
              item_id: null, item_code: null, item_name: ''
            })}
            placeholder="Type"
          />
        )
      },
      {
        title: 'Item Desc', dataIndex: 'item_name', width: 240,
        render: (v, row) => {
          const curName = row.item_name || row.itemName || '';
          const curId   = row.item_id   || row.itemId   || null;
          // SERVICE / OTHER stay free-text; products must be master-picked.
          if (MASTER_OPTIONAL_TYPES.has(row.item_type)) {
            return (
              <Input
                size="small"
                variant="borderless"
                value={curName}
                disabled={isLocked}
                maxLength={500}
                placeholder="Item description"
                onChange={(e) => patchLine(row._clientRowId,
                  { item_id: null, item_code: null, item_name: e.target.value })}
              />
            );
          }
          return (
            <ItemMasterSelect
              value={curId || undefined}
              label={curName}
              disabled={isLocked}
              onClear={() => patchLine(row._clientRowId,
                { item_id: null, item_code: null, item_name: '' })}
              onSelectItem={(id, item) => patchLine(row._clientRowId, {
                item_id:   id,
                item_code: item ? (item.itemCode || null) : null,
                item_name: item ? (item.itemName || curName) : curName,
                uom: row.uom || (item && item.defaultUomCode) || 'PCS',
                rate: Number(row.rate) > 0
                  ? row.rate : (item && Number(item.purchaseRate)) || row.rate,
                gst_rate: Number(row.gst_rate) > 0
                  ? row.gst_rate : (item && Number(item.gstRatePercent)) || row.gst_rate
              })}
            />
          );
        }
      },
      ] },
      // ── Glass dimension groups (shown only when a GLASS line exists) ──
      ...(hasGlassLines ? [
      { title: 'Size (mm)', onHeaderCell: () => ({ className: 'po-gh-blue' }), children: [
      {
        title: 'W', dataIndex: 'width_mm', width: 80,
        onCell: () => numCellProps,
        render: (v, row) => {
          if (!GLASS_TYPES.has(row.item_type)) return null;
          return (
            <InputNumber
              size="small"
              variant="borderless"
              controls={false}
              min={0}
              step={1}
              value={v ?? null}
              disabled={isLocked}
              style={{ width: '100%' }}
              onChange={(n) => patchLine(row._clientRowId,
                { width_mm: n == null ? 0 : Number(n) })}
            />
          );
        }
      },
      {
        title: 'H', dataIndex: 'height_mm', width: 80,
        onCell: () => numCellProps,
        render: (v, row) => {
          if (!GLASS_TYPES.has(row.item_type)) return null;
          return (
            <InputNumber
              size="small"
              variant="borderless"
              controls={false}
              min={0}
              step={1}
              value={v ?? null}
              disabled={isLocked}
              style={{ width: '100%' }}
              onChange={(n) => patchLine(row._clientRowId,
                { height_mm: n == null ? 0 : Number(n) })}
            />
          );
        }
      },
      ] },
      { title: 'Area', onHeaderCell: () => ({ className: 'po-gh-blue' }), children: [
      {
        title: 'SQMT', dataIndex: 'sqmt', width: 90,
        onCell: () => readonlyCellProps,
        render: (v, row) => GLASS_TYPES.has(row.item_type) ? dim(v) : null
      },
      {
        title: 'SQFT', dataIndex: 'sqft', width: 90,
        onCell: () => readonlyCellProps,
        render: (v, row) => GLASS_TYPES.has(row.item_type) ? dim(v) : null
      },
      ] }
      ] : []),
      // ── Length group (shown only when a PROFILE/UPVC/ALUMINIUM line exists) ──
      ...(hasLengthLines ? [
      { title: 'Length', onHeaderCell: () => ({ className: 'po-gh-blue' }), children: [
      {
        title: 'Len', dataIndex: 'length_value', width: 90,
        onCell: () => numCellProps,
        render: (v, row) => {
          if (!LENGTH_TYPES.has(row.item_type)) return null;
          return (
            <InputNumber
              size="small"
              variant="borderless"
              controls={false}
              min={0}
              step={0.01}
              value={v ?? null}
              disabled={isLocked}
              style={{ width: '100%' }}
              onChange={(n) => patchLine(row._clientRowId,
                { length_value: n == null ? 0 : Number(n) })}
            />
          );
        }
      },
      {
        title: 'UOM', dataIndex: 'length_uom', width: 90,
        render: (v, row) => {
          if (!LENGTH_TYPES.has(row.item_type)) return null;
          // The length UOM mirrors the line UOM when valid; we let the
          // operator override it via this dedicated dropdown.
          const current = v || (LENGTH_BASED_UOMS.includes(row.uom)
            ? row.uom : undefined);
          return (
            <Select
              size="small"
              variant="borderless"
              value={current}
              disabled={isLocked}
              style={{ width: '100%' }}
              placeholder="UOM"
              options={LENGTH_UOM_OPTIONS}
              onChange={(val) => patchLine(row._clientRowId,
                { length_uom: val, uom: row.uom || val })}
            />
          );
        }
      },
      {
        title: 'Total', dataIndex: 'total_length', width: 100,
        onCell: () => readonlyCellProps,
        render: (v, row) => LENGTH_TYPES.has(row.item_type) ? dim(v) : null
      },
      ] }
      ] : []),
      { title: 'Quantity', children: [
      // ── Always-visible columns ───────────────────────────────
      {
        title: 'Qty', dataIndex: 'qty', width: 90,
        onCell: () => numCellProps,
        render: (v, row) => (
          <InputNumber
            size="small"
            variant="borderless"
            controls={false}
            min={0}
            step={1}
            value={v ?? null}
            disabled={isLocked}
            style={{ width: '100%' }}
            onChange={(n) => patchLine(row._clientRowId,
              { qty: n == null ? 0 : Number(n) })}
          />
        )
      },
      {
        title: 'UOM', dataIndex: 'uom', width: 100,
        render: (v, row) => {
          // Glass lines surface SQMT/SQFT/PCS-style options; length
          // lines also see RFT/RMT/MTR; everyone else sees the full
          // list. We keep it simple and show all options regardless —
          // the validator on the server is the gate.
          const opts = UOM_OPTIONS;
          return (
            <Select
              size="small"
              variant="borderless"
              value={v || undefined}
              disabled={isLocked}
              style={{ width: '100%' }}
              showSearch
              options={opts}
              filterOption={(input, opt) =>
                String(opt?.value || '').toLowerCase()
                  .includes(input.toLowerCase())}
              placeholder="UOM"
              onChange={(val) => patchLine(row._clientRowId, { uom: val })}
            />
          );
        }
      },
      ] },
      { title: 'Rate', children: [
      {
        title: 'Rate', dataIndex: 'rate', width: 110,
        onCell: () => numCellProps,
        render: (v, row) => (
          <InputNumber
            size="small"
            variant="borderless"
            controls={false}
            min={0}
            step={0.01}
            value={v ?? null}
            disabled={isLocked}
            style={{ width: '100%' }}
            onChange={(n) => patchLine(row._clientRowId,
              { rate: n == null ? 0 : Number(n) })}
          />
        )
      },
      {
        title: 'Disc %', dataIndex: 'discount_percent', width: 80,
        onCell: () => numCellProps,
        render: (v, row) => (
          <InputNumber
            size="small"
            variant="borderless"
            controls={false}
            min={0}
            max={100}
            step={0.5}
            value={v ?? null}
            disabled={isLocked}
            style={{ width: '100%' }}
            onChange={(n) => patchLine(row._clientRowId,
              { discount_percent: n == null ? 0 : Number(n) })}
          />
        )
      },
      {
        title: 'Disc', dataIndex: 'discount_amount', width: 90,
        onCell: () => readonlyCellProps,
        render: (v) => money(v)
      },
      ] },
      { title: 'Tax', onHeaderCell: () => ({ className: 'po-gh-green' }), children: [
      {
        title: 'Taxable', dataIndex: 'taxable_amount', width: 110,
        onCell: () => readonlyCellProps,
        render: (v) => money(v)
      },
      {
        title: 'GST %', dataIndex: 'gst_rate', width: 90,
        render: (v, row) => (
          <Select
            size="small"
            variant="borderless"
            value={v ?? 0}
            disabled={isLocked}
            style={{ width: '100%' }}
            options={GST_RATE_OPTIONS}
            onChange={(val) => patchLine(row._clientRowId,
              { gst_rate: Number(val) })}
          />
        )
      },
      ] },
      { title: 'Schedule & notes', children: [
      {
        title: 'Expected Delivery', dataIndex: 'expected_delivery_date', width: 140,
        render: (v, row) => {
          const d = isoToDayjs(v);
          return (
            <DatePicker
              size="small"
              variant="borderless"
              value={d}
              disabled={isLocked}
              style={{ width: '100%' }}
              format="DD-MMM-YY"
              onChange={(val) => patchLine(row._clientRowId, {
                expected_delivery_date: dayjsToIso(val)
              })}
              placeholder="—"
            />
          );
        }
      },
      {
        title: 'Remarks', dataIndex: 'remarks', width: 180,
        render: (v, row) => (
          <Input
            size="small"
            variant="borderless"
            value={v || ''}
            disabled={isLocked}
            maxLength={500}
            placeholder="Notes"
            onChange={(e) => patchLine(row._clientRowId,
              { remarks: e.target.value })}
          />
        )
      },
      ] },
      {
        title: 'Line Total', dataIndex: 'line_total', width: 120,
        onCell: () => ({ className: 'po-cell-readonly po-cell-num po-cell-total' }),
        render: (v) => money(v)
      },
      {
        title: '', dataIndex: '_actions', width: 44, fixed: 'right',
        render: (_, row) => {
          if (isLocked) return null;
          const items = [
            {
              key: 'duplicate',
              label: 'Duplicate line',
              icon: <PlusOutlined />
            },
            { type: 'divider' },
            {
              key: 'delete',
              label: 'Delete line',
              icon: <DeleteOutlined />,
              danger: true
            }
          ];
          return (
            <Dropdown
              trigger={['click']}
              menu={{
                items,
                onClick: ({ key }) => {
                  if (key === 'duplicate') duplicateLine(row._clientRowId);
                  if (key === 'delete')    removeLine(row._clientRowId);
                }
              }}
            >
              <Button size="small" type="text" icon={<MoreOutlined />} />
            </Dropdown>
          );
        }
      }
    ].filter((c) => !Array.isArray(c.children) || c.children.length > 0);
  }, [isLocked, patchLine, duplicateLine, removeLine, hasGlassLines, hasLengthLines]);

  // ── Action gates for the toolbar ───────────────────────────────
  const showPlace  = !isCreate && canPlacePurchaseOrder(header);
  const showCancel = !isCreate && canCancelPurchaseOrder(header);
  const showClose  = !isCreate && canClosePurchaseOrder(header);

  const statusTag = (
    <span className="po-status po-status--head">
      <span className="po-sdot" style={{ background: STATUS_DOT[header.status || 'DRAFT'] || '#94a3b8' }} />
      <span className="po-sname">{statusLabel(header.status || 'DRAFT')}</span>
    </span>
  );

  const dirtyCount = useMemo(
    () => lines.filter((l) => l._dirty).length, [lines]);

  // ── Readiness checklist (presentation only) ─────────────────────
  // Mirrors what validatePurchaseOrder will require, surfaced as a
  // visible gate so the operator can see what's left before saving.
  // It never blocks — the Save / Place handlers stay the source of
  // truth and re-run validation as before.
  const supplierNameW = billFrom.name;
  const poDateW       = Form.useWatch('poDate',       form);
  const checks = useMemo(() => {
    const supplierOk = !!(supplierNameW && String(supplierNameW).trim());
    const dateOk     = !!poDateW || !!header.poDate;
    const linesOk    = lines.some((l) =>
      Number(l.qty) > 0 && (l.item_name || l.itemName || l.item_id || l.itemId));
    return [
      { label: 'PO date set',       ok: dateOk,
        hint: dateOk ? '' : 'required' },
      { label: 'Supplier named',    ok: supplierOk,
        hint: supplierOk ? String(supplierNameW).trim() : 'add a supplier' },
      { label: 'At least one line', ok: linesOk,
        hint: linesOk ? `${lines.length} line${lines.length === 1 ? '' : 's'}` : 'add a line' }
    ];
  }, [supplierNameW, poDateW, header.poDate, lines]);
  const readyCount = checks.filter((c) => c.ok).length;

  // GST split visibility for the rail summary.
  const showCgstSgst = (Number(totals.cgstAmount) + Number(totals.sgstAmount)) > 0;
  const showIgst     = Number(totals.igstAmount) > 0;

  return (
    <div className="po-shell">
      <Spin spinning={loading}>
        {isLocked && (
          <Alert
            type="info"
            showIcon
            message={`Purchase order is ${statusLabel(header.status)} — fields are read-only.`}
            description={
              header.status === 'CANCELLED' && header.cancellationReason
                ? `Cancellation reason: ${header.cancellationReason}`
                : null
            }
          />
        )}

        {/* ── Single column: document cards, then the bottom summary ── */}
        <div className="po-main">

          {/* ── Document column ──────────────────────────────── */}
          <Form
            form={form}
            layout="vertical"
            disabled={isLocked}
            initialValues={headerToFormValues(header)}
            className="po-doc"
          >
            {/* 1 — Order & supplier (merged) */}
            <div className="po-step po-step--first">
              <div className="po-step__rail" aria-hidden>
                <span className="po-step__node">1</span>
              </div>
              <div className="po-step__body">
              <div className="po-card">
              <div className="po-card__hd">
                <span className="po-card__t">Order &amp; supplier</span>
                <span className="po-card__hint">PO number is issued automatically on save</span>
              </div>
              <div className="po-card__bd">
                <div className="po-merge">

                  {/* Order block */}
                  <div className="po-merge__col">
                    <div className="po-merge__sub">
                      <span className="po-merge__ico"><FileTextOutlined /></span>Order
                    </div>
                    <Form.Item label="PO Date" name="poDate"
                      rules={[{ required: true, message: 'PO date is required.' }]}>
                      <DatePicker style={{ width: '100%' }} format="DD-MMM-YYYY" />
                    </Form.Item>
                    <Form.Item label="Expected" name="expectedDeliveryDate">
                      <DatePicker style={{ width: '100%' }} format="DD-MMM-YYYY" />
                    </Form.Item>
                    <Form.Item label="Source PR">
                      <Input
                        value={header.prNumberSnapshot || header.sourcePrId || ''}
                        disabled
                        placeholder="—"
                      />
                    </Form.Item>
                    <Form.Item label="Deliver to" name="deliveryLocation">
                      <Input maxLength={500} placeholder="Site / warehouse" />
                    </Form.Item>
                  </div>

                  <div className="po-merge__div" />

                  {/* Supplier block — same field format as Ship To, master-only */}
                  <div className="po-merge__col">
                    <div className="po-merge__sub">
                      <span className="po-merge__ico"><ShopOutlined /></span>Supplier
                    </div>
                    <div className="po-ff">
                      <span className="po-ff__l">Name<span className="req">*</span></span>
                      <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                        <SupplierMasterSelect
                          value={header.supplierId || undefined}
                          label={billFrom.name}
                          disabled={isLocked}
                          placeholder="Search Supplier Master…"
                          onSelectSupplier={(sid, sup) => {
                            setHeader((prev) => Object.assign({}, prev, { supplierId: sid }));
                            // The picker hands back the full master row as `sup.raw`
                            // (the list selects s.*), so autofill the whole address.
                            const raw = (sup && sup.raw) || {};
                            setBillFrom((prev) => Object.assign({}, prev, {
                              name:         (sup && sup.supplierName) || raw.supplierName || prev.name || '',
                              addressLine1: raw.addressLine1 || '',
                              addressLine2: raw.addressLine2 || '',
                              city:         raw.city || '',
                              pincode:      raw.pincode || '',
                              state:        raw.state || raw.gstStateName || '',
                              stateCode:    (sup && sup.supplierStateCode) || raw.gstStateCode || raw.stateCode || prev.stateCode || '',
                              mobile:       raw.mobile || raw.phone || raw.alternateMobile || '',
                              email:        raw.email || '',
                              gstin:        (sup && sup.supplierGstin) || raw.gstin || prev.gstin || ''
                            }));
                          }}
                          onClear={() => {
                            setHeader((prev) => Object.assign({}, prev, { supplierId: null }));
                            setBillFrom((prev) => Object.assign({}, prev,
                              { name: '', gstin: '', stateCode: '' }));
                          }}
                        />
                      </div>
                    </div>
                    <div className="po-ff">
                      <span className="po-ff__l">Add</span>
                      <Input variant="borderless" className="po-ff__i" maxLength={500}
                        value={billFrom.addressLine1 || ''} disabled={isLocked}
                        onChange={(e) => patchBillFrom('addressLine1', e.target.value)} />
                    </div>
                    <div className="po-ff">
                      <span className="po-ff__l">Add 2</span>
                      <Input variant="borderless" className="po-ff__i" maxLength={500}
                        value={billFrom.addressLine2 || ''} disabled={isLocked}
                        onChange={(e) => patchBillFrom('addressLine2', e.target.value)} />
                    </div>
                    <div className="po-pair">
                      <div className="po-pair__grow po-ff">
                        <span className="po-ff__l">City</span>
                        <Input variant="borderless" className="po-ff__i" maxLength={120}
                          value={billFrom.city || ''} disabled={isLocked}
                          onChange={(e) => patchBillFrom('city', e.target.value)} />
                      </div>
                      <div className="po-pair__fix">
                        <span className="po-ff__l">Pincode</span>
                        <Input variant="borderless" className="po-ff__i" maxLength={12}
                          value={billFrom.pincode || ''} disabled={isLocked}
                          onChange={(e) => patchBillFrom('pincode', e.target.value)} />
                      </div>
                    </div>
                    <div className="po-pair">
                      <div className="po-pair__grow po-ff">
                        <span className="po-ff__l">State</span>
                        <Input variant="borderless" className="po-ff__i" maxLength={2}
                          value={billFrom.stateCode || ''} disabled={isLocked}
                          placeholder="—"
                          onChange={(e) => patchBillFrom('stateCode', e.target.value.toUpperCase())} />
                      </div>
                      <div className="po-pair__fix mob">
                        <span className="po-ff__l">Mobile</span>
                        <Input variant="borderless" className="po-ff__i" maxLength={40}
                          value={billFrom.mobile || ''} disabled={isLocked}
                          placeholder="98xxxxxxxx, …"
                          onChange={(e) => patchBillFrom('mobile', e.target.value)} />
                      </div>
                    </div>
                    <div className="po-ff">
                      <span className="po-ff__l">Email</span>
                      <Input variant="borderless" className="po-ff__i" maxLength={160}
                        value={billFrom.email || ''} disabled={isLocked}
                        onChange={(e) => patchBillFrom('email', e.target.value)} />
                    </div>
                    <div className="po-gst">
                      <span className="po-ff__l">GSTIN<span className="req">*</span></span>
                      <Input variant="borderless" className="po-ff__i po-gst__grow" maxLength={15}
                        value={billFrom.gstin || ''} disabled={isLocked}
                        placeholder="GSTIN / PAN / Aadhaar / URP"
                        onChange={(e) => patchBillFrom('gstin', e.target.value.toUpperCase())} />
                      <div className="po-sc">
                        <span className="po-ff__l">SC</span>
                        <Input variant="borderless" className="po-ff__i" maxLength={2}
                          value={billFrom.stateCode || ''} disabled={isLocked}
                          onChange={(e) => patchBillFrom('stateCode', e.target.value.toUpperCase())} />
                      </div>
                      <div className="po-sc">
                        <span className="po-ff__l">Co.SC</span>
                        <Input variant="borderless" className="po-ff__i" maxLength={2}
                          value={companyStateCode || ''} disabled={isLocked}
                          onChange={(e) => setCompanyStateCode(e.target.value.toUpperCase())} />
                      </div>
                    </div>
                  </div>

                  <div className="po-merge__div" />

                  {/* Ship To — structured delivery address (where goods land) */}
                  <div className="po-merge__col">
                    <div className="po-merge__sub">
                      <span className="po-merge__ico"><EnvironmentOutlined /></span>Ship to
                    </div>
                    <div className="po-ff">
                      <span className="po-ff__l">Name</span>
                      <Input variant="borderless" className="po-ff__i" maxLength={220}
                        value={shipTo.name || ''} disabled={isLocked}
                        placeholder="Delivery site / contact"
                        onChange={(e) => patchShipTo('name', e.target.value)} />
                    </div>
                    <div className="po-ff">
                      <span className="po-ff__l">Add</span>
                      <Input variant="borderless" className="po-ff__i" maxLength={500}
                        value={shipTo.addressLine1 || ''} disabled={isLocked}
                        onChange={(e) => patchShipTo('addressLine1', e.target.value)} />
                    </div>
                    <div className="po-ff">
                      <span className="po-ff__l">Add 2</span>
                      <Input variant="borderless" className="po-ff__i" maxLength={500}
                        value={shipTo.addressLine2 || ''} disabled={isLocked}
                        onChange={(e) => patchShipTo('addressLine2', e.target.value)} />
                    </div>
                    <div className="po-pair">
                      <div className="po-pair__grow po-ff">
                        <span className="po-ff__l">City</span>
                        <Input variant="borderless" className="po-ff__i" maxLength={120}
                          value={shipTo.city || ''} disabled={isLocked}
                          onChange={(e) => patchShipTo('city', e.target.value)} />
                      </div>
                      <div className="po-pair__fix">
                        <span className="po-ff__l">Pincode</span>
                        <Input variant="borderless" className="po-ff__i" maxLength={12}
                          value={shipTo.pincode || ''} disabled={isLocked}
                          onChange={(e) => patchShipTo('pincode', e.target.value)} />
                      </div>
                    </div>
                    <div className="po-pair">
                      <div className="po-pair__grow po-ff">
                        <span className="po-ff__l">State</span>
                        <Input variant="borderless" className="po-ff__i" maxLength={2}
                          value={shipTo.stateCode || ''} disabled={isLocked}
                          placeholder="—"
                          onChange={(e) => patchShipTo('stateCode', e.target.value.toUpperCase())} />
                      </div>
                      <div className="po-pair__fix mob">
                        <span className="po-ff__l">Mobile</span>
                        <Input variant="borderless" className="po-ff__i" maxLength={40}
                          value={shipTo.mobile || ''} disabled={isLocked}
                          placeholder="98xxxxxxxx, …"
                          onChange={(e) => patchShipTo('mobile', e.target.value)} />
                      </div>
                    </div>
                    <div className="po-ff">
                      <span className="po-ff__l">Email</span>
                      <Input variant="borderless" className="po-ff__i" maxLength={160}
                        value={shipTo.email || ''} disabled={isLocked}
                        onChange={(e) => patchShipTo('email', e.target.value)} />
                    </div>
                    <div className="po-gst">
                      <span className="po-ff__l">GSTIN</span>
                      <Input variant="borderless" className="po-ff__i po-gst__grow" maxLength={15}
                        value={shipTo.gstin || ''} disabled={isLocked}
                        placeholder="GSTIN / PAN / Aadhaar / URP"
                        onChange={(e) => patchShipTo('gstin', e.target.value.toUpperCase())} />
                      <div className="po-sc">
                        <span className="po-ff__l">SC</span>
                        <Input variant="borderless" className="po-ff__i" maxLength={2}
                          value={shipTo.stateCode || ''} disabled={isLocked}
                          onChange={(e) => patchShipTo('stateCode', e.target.value.toUpperCase())} />
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            </div>
              </div>
            </div>

            {/* 2 — Line items */}
            <div className="po-step">
              <div className="po-step__rail" aria-hidden>
                <span className="po-step__node">2</span>
              </div>
              <div className="po-step__body">
              <div className="po-lines-card">
              <div className="po-lines-card__head">
                <span className="po-lines-title">Line items</span>
                <span className="po-lines-spacer" />
                {(hasGlassLines || hasLengthLines) && (
                  <span className="po-lines-hint">Scroll → to see all columns</span>
                )}
                <span className="po-lines-count">
                  <span className="po-sdot" />{lines.length} line{lines.length === 1 ? '' : 's'}
                </span>
              </div>
              <Table
                className="po-grid"
                size="small"
                rowKey="_clientRowId"
                dataSource={lines}
                columns={columns}
                pagination={false}
                sticky
                scroll={{ x: 'max-content' }}
                rowClassName={(r) => r._dirty ? 'po-dirty-row' : ''}
                locale={{
                  emptyText: isLocked
                    ? 'No lines on this purchase order.'
                    : 'No lines yet — click Add Line to start.'
                }}
              />
              {!isLocked && (
                <div className="po-addrow">
                  <Button size="small" icon={<PlusOutlined />} onClick={addLine}>
                    Add line
                  </Button>
                </div>
              )}
            </div>
              </div>
            </div>

            {/* 3 — Terms & notes */}
            <div className="po-step">
              <div className="po-step__rail" aria-hidden>
                <span className="po-step__node">3</span>
              </div>
              <div className="po-step__body">
              <div className="po-card">
              <div className="po-card__hd">
                <span className="po-card__t">Terms &amp; notes</span>
                <span className="po-card__hint">Optional</span>
              </div>
              <div className="po-card__bd">
                <div className="grid grid-cols-12 gap-x-3">
                  <div className="col-span-3">
                    <Form.Item label="Payment Terms" name="paymentTerms">
                      <Input maxLength={500} placeholder="e.g., 30 days net" />
                    </Form.Item>
                  </div>
                  <div className="col-span-3">
                    <Form.Item label="Delivery Terms" name="deliveryTerms">
                      <Input maxLength={500} placeholder="e.g., DDP site" />
                    </Form.Item>
                  </div>
                  <div className="col-span-3">
                    <Form.Item label="Freight Terms" name="freightTerms">
                      <Input maxLength={500} placeholder="e.g., To-pay" />
                    </Form.Item>
                  </div>
                  <div className="col-span-3">
                    <Form.Item label="Remarks" name="remarks">
                      <Input maxLength={2000} placeholder="Header notes" />
                    </Form.Item>
                  </div>
                </div>
              </div>
            </div>
              </div>
            </div>
          </Form>

          {/* ── Order summary — terminal node of the step spine ───────── */}
          <div className="po-step po-step--sum">
            <div className="po-step__rail" aria-hidden>
              <span className="po-step__node is-sum">∑</span>
            </div>
            <div className="po-step__body">
          <section className="po-bottom" aria-label="Order summary">
            <div className="po-bottom__head">
              <span className="po-bottom__title">Order summary</span>
              {statusTag}
              {!isLocked && dirtyCount > 0 && (
                <span className="po-stat-chip po-stat-chip--dirty">
                  <strong>{dirtyCount}</strong> unsaved
                </span>
              )}
              {isLocked && (
                <span className="po-stat-chip"><LockOutlined /> locked</span>
              )}
              <span className="po-bottom__to">
                {header.supplierName ? <>To {header.supplierName}</> : 'No supplier selected yet'}
              </span>
            </div>

            <div className="po-bottom__body">

              {/* Other charges — ad-hoc additional cost (Amount = Area × Rs, no GST).
                  A trailing "Pick charge…" row is always shown to add a new one. */}
              <div className="po-oc">
                <div className="po-oc__cap">
                  Other charges <span className="po-oc__cap-sub">· manual</span>
                </div>
                <div className="po-oc__scroll">
                  <table className="po-oc__table">
                    <thead>
                      <tr>
                        <th className="l">Charge</th>
                        <th className="c">By</th>
                        <th className="r">Area</th>
                        <th className="r">Value</th>
                        <th className="r">Amount</th>
                        <th aria-label="actions" />
                      </tr>
                    </thead>
                    <tbody>
                      {otherCharges.map((c) => (
                        <tr key={c._clientRowId}>
                          <td className="l">
                            <Select
                              size="small"
                              variant="borderless"
                              showSearch
                              value={c.chargeName || undefined}
                              disabled={isLocked}
                              placeholder="Pick charge…"
                              options={chargeSelectOptions}
                              optionFilterProp="label"
                              style={{ width: '100%' }}
                              notFoundContent={chargeOptions.length === 0
                                ? 'No charges in master' : 'No match'}
                              onChange={(v) => pickCharge(c._clientRowId, v)}
                            />
                          </td>
                          <td className="c po-oc__by rs">Rs.</td>
                          <td className="r po-oc__cell">
                            <InputNumber
                              size="small"
                              variant="borderless"
                              controls={false}
                              min={0}
                              value={c.area ?? null}
                              disabled={isLocked}
                              placeholder="0.000"
                              onChange={(n) => patchCharge(c._clientRowId,
                                { area: n == null ? 0 : Number(n) })}
                            />
                          </td>
                          <td className="r po-oc__edit">
                            <InputNumber
                              size="small"
                              variant="borderless"
                              controls={false}
                              min={0}
                              prefix="₹"
                              value={c.rate ?? null}
                              disabled={isLocked}
                              placeholder="0"
                              onChange={(n) => patchCharge(c._clientRowId,
                                { rate: n == null ? 0 : Number(n) })}
                            />
                          </td>
                          <td className="r po-oc__amt">{money(chargeAmount(c))}</td>
                          <td className="c">
                            {!isLocked && (
                              <Button size="small" type="text" danger
                                icon={<DeleteOutlined />}
                                onClick={() => removeCharge(c._clientRowId)} />
                            )}
                          </td>
                        </tr>
                      ))}
                      {!isLocked && (
                        <tr className="po-oc__adder">
                          <td className="l">
                            <span className="po-oc__addmk" aria-hidden>+</span>
                            <Select
                              key={`oc-adder-${otherCharges.length}`}
                              size="small"
                              variant="borderless"
                              showSearch
                              value={undefined}
                              placeholder="Add a charge…"
                              options={chargeSelectOptions}
                              optionFilterProp="label"
                              notFoundContent={chargeOptions.length === 0
                                ? 'No charges in master' : 'No match'}
                              onChange={(v) => addChargeWithName(v)}
                            />
                          </td>
                          <td className="c po-oc__muted">—</td>
                          <td className="r po-oc__muted">0.000</td>
                          <td className="r po-oc__muted">₹&nbsp;0</td>
                          <td className="r po-oc__muted">0.00</td>
                          <td />
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <div className="po-oc__foot">
                  <span className="po-oc__foot-k">Charges total</span>
                  <span className="po-oc__total">{money(totals.otherChargesAmount)}</span>
                </div>
              </div>

              {/* totals breakdown (grid style; mirrors Other charges theme) */}
              <div className="po-sum">
                <div className="po-sum__cap">Summary</div>
                <table className="po-sum__table">
                  <tbody>
                    <tr>
                      <td className="k">Subtotal</td>
                      <td className="v">{money(totals.subtotal)}</td>
                    </tr>
                    <tr>
                      <td className="k">Discount</td>
                      <td className="v">{money(totals.discountAmount)}</td>
                    </tr>
                    <tr>
                      <td className="k">Taxable value</td>
                      <td className="v">{money(totals.taxableAmount)}</td>
                    </tr>
                    {showCgstSgst && (
                      <>
                        <tr className="sub">
                          <td className="k">CGST</td>
                          <td className="v">{money(totals.cgstAmount)}</td>
                        </tr>
                        <tr className="sub">
                          <td className="k">SGST</td>
                          <td className="v">{money(totals.sgstAmount)}</td>
                        </tr>
                      </>
                    )}
                    {showIgst && (
                      <tr className="sub">
                        <td className="k">IGST</td>
                        <td className="v">{money(totals.igstAmount)}</td>
                      </tr>
                    )}
                    <tr>
                      <td className="k">Total GST</td>
                      <td className="v">{money(totals.totalTax)}</td>
                    </tr>
                    <tr className="oc">
                      <td className="k">Other charges</td>
                      <td className="v">{money(totals.otherChargesAmount)}</td>
                    </tr>
                    <tr className="roundoff">
                      <td className="k">Round off</td>
                      <td className="v edit">
                        <InputNumber
                          className="po-roundoff"
                          size="small"
                          variant="borderless"
                          controls={false}
                          step={0.01}
                          value={roundOff}
                          disabled={isLocked}
                          onChange={(n) => setRoundOff(n == null ? 0 : Number(n))}
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* grand total + docked actions */}
              <div className="po-bottom__pay">
                <div className="po-grand">
                  <div className="po-grand__lab">Grand total</div>
                  <div className="po-grand__amt">₹{money(totals.grandTotal)}</div>
                  <div className="po-grand__sub">incl. {money(totals.totalTax)} GST</div>
                </div>
                <div className="po-bottom__act">
                  {!isLocked && (
                    <Button type="primary" icon={<SaveOutlined />}
                      onClick={saveDraft} loading={saving}>
                      Save draft
                    </Button>
                  )}
                  {showPlace && (
                    <Button className="po-place-btn" icon={<SendOutlined />}
                      onClick={placePo} loading={acting}>
                      Place PO
                    </Button>
                  )}
                  {showClose && (
                    <Button icon={<CheckCircleOutlined />}
                      onClick={closePo} loading={acting}>
                      Close PO
                    </Button>
                  )}
                  {showCancel && (
                    <Button danger icon={<StopOutlined />}
                      onClick={openCancelModal} disabled={acting}>
                      Cancel PO
                    </Button>
                  )}
                </div>
                <div className="po-bottom__note">
                  The backend re-checks every figure to the paisa on save.
                </div>
              </div>
            </div>
          </section>
            </div>
          </div>
        </div>
      </Spin>

      {/* ── Cancel modal ──────────────────────────────────────── */}
      <Modal
        open={cancelOpen}
        title="Cancel purchase order?"
        okText="Cancel PO"
        okType="danger"
        cancelText="Back"
        confirmLoading={acting}
        onCancel={() => setCancelOpen(false)}
        onOk={confirmCancel}
      >
        <p className="text-sm text-slate-600 mb-2">
          Cancelling marks the PO and all its lines as cancelled. This action
          can be reversed only by re-creating a new PO.
        </p>
        <Input.TextArea
          rows={3}
          maxLength={2000}
          value={cancelReason}
          onChange={(e) => setCancelReason(e.target.value)}
          placeholder="Optional cancellation reason"
        />
      </Modal>
    </div>
  );
}

